import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Grid, Card, IconButton, Tooltip, CircularProgress, Box, Typography, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Button, Divider, Stack, Snackbar, Alert, TextField, Popper, ClickAwayListener } from "@mui/material";
import { Visibility, Download, Add, CloudDownload, Edit, ArrowForward, Description, PictureAsPdf } from "@mui/icons-material";
import { MaterialReactTable } from "material-react-table";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAutocomplete from "components/MDAutocomplete";
import { useQuery } from "@tanstack/react-query";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useNavigate, useLocation } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import { IconTableExport } from "@tabler/icons-react";
import handleExport from "utils/exportData";
import PropTypes from "prop-types";

function ClaimsList() {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [exportData, setExportData] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [message, setMessage] = useState({
    status: "",
    title: "",
    msg: "",
  });
  const [viewPdfUrl, setViewPdfUrl] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openMonthPicker, setOpenMonthPicker] = useState(false);

  const handleClosePdf = () => setViewPdfUrl(null);

  // Date limits
  const minDateLimit = dayjs().subtract(1, 'year').startOf('year');

  // Fetch current user
  const me = async () => {
    const response = await axios({
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/auth/me`,
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    });
    return response.data;
  };

  const { data: meData } = useQuery({
    queryKey: ["me"],
    queryFn: me,
    retry: false,
  });

  const [filters, setFilters] = useState({
    fromMonth: null,
    toMonth: null,
    userName: "",
    district: "",
    claimType: "",
  });

  const claimOptions = [
    { title: "All", value: "" },
    { title: "All Submitted", value: "all_submitted" },
    { title: "All Not Submitted", value: "all_not_submitted" },
    { title: "Attendance Sheet - Submitted Users", value: "attendance_sheet_submitted" },
    { title: "Attendance Sheet - Not submitted Users", value: "attendance_sheet_not_submitted" },
    { title: "Stipend Claim - Submitted Users", value: "stipend_claim_submitted" },
    { title: "Stipend Claim - Not submitted Users", value: "stipend_claim_not_submitted" },
    { title: "Travel Bills - Submitted Users", value: "travel_bills_submitted" },
    { title: "Travel Bills - Not submitted Users", value: "travel_bills_not_submitted" },
    { title: "Review TA Bills - Submitted Users", value: "review_ta_bills_submitted" },
    { title: "Review TA Bills - Not submitted Users", value: "review_ta_bills_not_submitted" },
  ];

  // Handle auto-filter from dashboard navigation
  useEffect(() => {
    if (location.state && meData) {
      const newFilters = { ...filters };

      if (location.state.claimType) {
        if (location.state.status === "Not Submitted") {
          newFilters.claimType = `${location.state.claimType}_not_submitted`;
        } else {
          newFilters.claimType = `${location.state.claimType}_submitted`;
        }
      } else if (location.state.status) {
        newFilters.claimType = location.state.status === "Not Submitted" ? "all_not_submitted" : "all_submitted";
      }

      if (location.state.month && location.state.year) {
        const monthDate = dayjs().year(location.state.year).month(location.state.month - 1);
        newFilters.fromMonth = monthDate.startOf('month');
        newFilters.toMonth = monthDate.endOf('month');
      }

      setFilters(newFilters);

      // Trigger fetch immediately with new filters
      fetchClaimsList(0, pagination.pageSize, newFilters, columnFilters);

      // Clear state to avoid re-applying on every render or back navigation
      window.history.replaceState({}, document.title);
    }
  }, [location.state, meData]);

  // Initialize filters once meData is available (Admin default view)
  useEffect(() => {
    if (meData?.role_id === 1 && !location.state) {
      const defaultFrom = dayjs().startOf('month');
      const defaultTo = dayjs().endOf('month');

      setFilters(prev => {
        // Apply defaults if missing
        const newFrom = prev.fromMonth || defaultFrom;
        const newTo = prev.toMonth || defaultTo;

        // Trigger initial fetch here because the main effect explicitly skips if filters are empty for admin
        // and we removed 'filters' from that dependency array.
        // We pass the calculated values directly to ensure fetch uses them immediately.
        fetchClaimsList(pagination.pageIndex, pagination.pageSize, { ...prev, fromMonth: newFrom, toMonth: newTo }, columnFilters);

        return {
          ...prev,
          fromMonth: newFrom,
          toMonth: newTo
        };
      });
    }
  }, [meData]);

  const fetchDistrict = async () => {
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/districts/all`, { withCredentials: true });
    return response.data.data;
  };

  const { data: districtData } = useQuery({
    queryKey: ["fetchDistrict"],
    queryFn: fetchDistrict,
    retry: false,
  });

  const fetchAllUsers = async () => {
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/user/role3`, { withCredentials: true });
    return response.data.data;
  };

  const { data: usersData } = useQuery({
    queryKey: ["fetchAllUsers"],
    queryFn: fetchAllUsers,
    retry: false,
  });

  const fetchClaimsList = async (page = 0, size = 10, filterValues = filters, colFilters = columnFilters) => {
    if (!meData) return;

    // For Admin: Ensure default dates are set if not present in filterValues (fallback)
    const currentFrom = filterValues.fromMonth || (meData.role_id === 1 ? dayjs().startOf('month') : null);
    const currentTo = filterValues.toMonth || (meData.role_id === 1 ? dayjs().endOf('month') : null);

    // If still loading meData logic or strict check needed, wait. 
    // But usually rendering happens after meData is present due to parent checks or effects.

    setLoading(true);
    try {
      let statusValue = "";
      let claimTypeValue = "";

      if (filterValues.claimType.includes('_submitted') || filterValues.claimType.includes('_not_submitted') || filterValues.claimType === "all_submitted" || filterValues.claimType === "all_not_submitted") {
        claimTypeValue = filterValues.claimType;
      } else if (filterValues.claimType === "Submitted" || filterValues.claimType === "Not Submitted") {
        // Handle potential legacy values
        statusValue = filterValues.claimType;
      } else {
        claimTypeValue = filterValues.claimType;
      }

      const queryParams = new URLSearchParams({
        page: page + 1,
        limit: size,
        from_month: currentFrom ? currentFrom.format("YYYY-MM") : "",
        to_month: currentTo ? currentTo.format("YYYY-MM") : "",
        user_name: filterValues.userName,
        district: filterValues.district,
        status: statusValue,
        claim_type: claimTypeValue,
        columnFilters: JSON.stringify(colFilters),
      });

      const url = (meData.role_id === 1 || meData.role_id === 2)
        ? `${process.env.REACT_APP_API_URL}/claims/list?${queryParams.toString()}`
        : `${process.env.REACT_APP_API_URL}/claims/list/${meData.user_id}?${queryParams.toString()}`;

      const res = await axios.get(url, { withCredentials: true });
      if (res.data.status === 200) {
        let fetchedData = res.data.data || [];

        // No mapping needed if backend returns User summary format directly
        // Based on user request data: { user_id, user_name, district_name, claims: [] }

        setData(fetchedData);
        setRowCount(res.data.totalCount || fetchedData.length);
        setExportData(fetchedData);
      }
    } catch (err) {
      console.error("Error fetching claims:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    // Skip if meData not ready or if we're handling a dashboard navigation (which has its own fetch)
    if (meData && !location.state) {
      // Only fetch if filters are initialized for admin to avoid empty call -> refresh issue
      if (meData.role_id === 1 && !filters.fromMonth) {
        // Wait for effect to set defaults
        return;
      }
      fetchClaimsList(pagination.pageIndex, pagination.pageSize, filters, columnFilters);
    }
  }, [pagination.pageIndex, pagination.pageSize, meData, columnFilters, sorting]); // Removed filters from dependency to prevent auto-fetch

  const handleSearch = () => {
    fetchClaimsList(0, pagination.pageSize, filters, columnFilters);
    setPagination(p => ({ ...p, pageIndex: 0 }));
  };

  const handleReset = () => {
    const resetFilters = {
      fromMonth: meData?.role_id === 1 ? dayjs().startOf('month') : null,
      toMonth: meData?.role_id === 1 ? dayjs().endOf('month') : null,
      userName: "",
      district: "",
      claimType: "",
    };
    setFilters(resetFilters);
    setPagination({ ...pagination, pageIndex: 0 });
    fetchClaimsList(0, pagination.pageSize, resetFilters, columnFilters);
  };

  const handleExportData = async () => {
    try {
      let rawExportData = [];
      if (rowSelection && Object.keys(rowSelection).length > 0) {
        rawExportData = exportData.filter((item, index) => rowSelection[item.user_id || index]);
      } else {
        const queryParams = new URLSearchParams({
          from_month: filters.fromMonth ? filters.fromMonth.format("YYYY-MM") : "",
          to_month: filters.toMonth ? filters.toMonth.format("YYYY-MM") : "",
          user_name: filters.userName,
          district: filters.district,
          page: 1,
          limit: 10000,
        });
        const url =
          meData.role_id === 1
            ? `${process.env.REACT_APP_API_URL}/claims/list?${queryParams.toString()}`
            : `${process.env.REACT_APP_API_URL}/claims/list/${meData.user_id}?${queryParams.toString()}`;

        const res = await axios.get(url, { withCredentials: true });
        if (res.data.status === 200) rawExportData = res.data.data;
      }

      // Flatten rawExportData into individual claim records
      let flattened = [];
      rawExportData.forEach((user) => {
        if (user.claims && Array.isArray(user.claims)) {
          user.claims.forEach((claim) => {
            flattened.push({
              ...claim,
              user_name: user.user_name,
              district_name: user.district_name,
            });
          });
        } else if (user.month) {
          // Already a claim record (e.g., regular user view)
          flattened.push(user);
        }
      });

      if (flattened.length === 0) {
        setMessage({ status: "error", title: "Error", msg: "No data found to export." });
        return;
      }

      await handleExport(flattened, "Claims_Records");
      setMessage({ status: "success", title: "Success", msg: "Excel downloaded successfully!" });
    } catch (error) {
      setMessage({ status: "error", title: "Error", msg: "Something went wrong during export." });
    }
  };

  const columns = useMemo(() => {
    const baseColumns = [];

    const userOptions = (usersData || []).map(
      (u) => u.user_name || u.title || ""
    );
    const districtOptions = (districtData || []).map(
      (d) => d.title || d.district_name || ""
    );

    const uniqueUserOptions = [...new Set(userOptions.filter(Boolean))];
    const uniqueDistrictOptions = [...new Set(districtOptions.filter(Boolean))];

    if (meData?.role_id === 1) {
      baseColumns.push(
        {
          accessorKey: "user_name",
          header: "User Name",
          size: 150,
          filterVariant: "select",
          filterSelectOptions: uniqueUserOptions,
          muiTableHeadCellProps: { align: "center" },
          muiTableBodyCellProps: { align: "center" },
        },
        {
          accessorKey: "district_name",
          header: "District",
          size: 150,
          filterVariant: "select",
          filterSelectOptions: uniqueDistrictOptions,
          muiTableHeadCellProps: { align: "center" },
          muiTableBodyCellProps: { align: "center" },
        },
        {
          id: "status",
          header: "Status",
          size: 150,
          filterVariant: "select",
          filterSelectOptions: ["Submitted", "Not Submitted"],
          muiTableHeadCellProps: { align: "center" },
          muiTableBodyCellProps: { align: "center" },
          Cell: ({ row }) => {
            const currentFilter = filters.claimType || "";
            let isSubmitted = false;

            // Only use document-aware status for specific document filters
            const isDocSpecificFilter = currentFilter.includes('_') &&
              !currentFilter.startsWith('all_');

            if (isDocSpecificFilter) {
              const baseDoc = currentFilter.replace('_submitted', '').replace('_not_submitted', '');
              const claimFieldMap = {
                'attendance_sheet': 'attendance_sheet',
                'stipend_claim': 'stipend_claim',
                'travel_bills': 'travel_bills',
                'review_ta_bills': 'review_ta_bills',
              };
              const field = claimFieldMap[baseDoc];

              if (field && row.original.claims && row.original.claims.length > 0) {
                isSubmitted = row.original.claims.some(c => {
                  const val = c[field];
                  if (!val) return false;
                  if (Array.isArray(val) && val.length === 0) return false;
                  if (typeof val === 'string' && (val === '[]' || val === '')) return false;
                  return true;
                });
              }
            } else {
              // Overall status logic for "All", "All Submitted", and "All Not Submitted"
              isSubmitted = row.original.claims && row.original.claims.length > 0;
            }

            return (
              <MDTypography
                variant="caption"
                fontWeight="bold"
                color={isSubmitted ? "success" : "error"}
              >
                {isSubmitted ? "Submitted" : "Not Submitted"}
              </MDTypography>
            );
          },
        },
        {
          id: "action",
          header: "Action",
          size: 100,
          muiTableHeadCellProps: { align: "center" },
          muiTableBodyCellProps: { align: "center" },
          Cell: ({ row }) => {
            const hasClaims =
              row.original.claims && row.original.claims.length > 0;

            if (!hasClaims) return null;

            return (
              <Tooltip title="View Details">
                <IconButton
                  color="info"
                  onClick={() =>
                    navigate(`/claims/list/${row.original.user_id}`, {
                      state: {
                        user_name: row.original.user_name,
                        district_name: row.original.district_name,
                        fromMonth: filters.fromMonth
                          ? filters.fromMonth.format("YYYY-MM-DD")
                          : null,
                        toMonth: filters.toMonth
                          ? filters.toMonth.format("YYYY-MM-DD")
                          : null,
                      },
                    })
                  }
                >
                  <Visibility />
                </IconButton>
              </Tooltip>
            );
          },
        }
      );
    }

    return baseColumns;
  }, [meData, navigate, filters, usersData, districtData]);


  const parseDocUrls = (docData) => {
    if (!docData) return [];
    let urls = [];
    try {
      if (typeof docData === 'string') {
        if (docData.startsWith('[') && docData.endsWith(']')) {
          urls = JSON.parse(docData);
        } else {
          urls = [docData];
        }
      } else if (Array.isArray(docData)) {
        urls = docData;
      }
    } catch (e) {
      urls = [docData];
    }
    return urls;
  };

  const handlePreview = (url) => {
    if (!url) return;
    const cleanUrl = url.split('?')[0].toLowerCase();
    const isWord = cleanUrl.endsWith('.doc') || cleanUrl.endsWith('.docx');

    if (isWord) {
      const officeUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
      setViewPdfUrl(officeUrl);
    } else {
      setViewPdfUrl(url);
    }
  };

  const handleDownload = async (url, title) => {
    if (!url) return;
    try {
      const cleanUrl = url.split('?')[0].toLowerCase();
      const originalExt = cleanUrl.split('.').pop();
      // Only download extensions that are not pdf
      // If it is a word doc, download as is.
      // Actually we just want to download the file directly with its extension.

      let downloadExt = originalExt;
      if (originalExt.startsWith('doc')) {
        downloadExt = originalExt;
      }

      const response = await fetch(url);
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `${title.replace(/\s+/g, '_')}_${dayjs().format('YYYYMMDD')}.${downloadExt}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setMessage({ status: "success", title: "Success", msg: "File downloaded successfully!" });
    } catch (error) {
      console.error("Download failed", error);
      setMessage({ status: "error", title: "Error", msg: "Download failed. Please try again." });
    }
  };

  const DocumentCell = ({ value, title }) => {
    const urls = parseDocUrls(value);
    if (!urls || urls.length === 0) return <MDTypography variant="caption" color="text">-</MDTypography>;
    return (
      <MDBox display="flex" flexDirection="column" gap={0.5} py={0.5}>
        {urls.map((url, index) => {
          const cleanUrl = url.split('?')[0].toLowerCase();
          const isPdf = cleanUrl.endsWith('.pdf');
          const isWord = cleanUrl.endsWith('.doc') || cleanUrl.endsWith('.docx');

          return (
            <MDBox key={index} display="flex" gap={1} alignItems="center">
              {isPdf ? <PictureAsPdf sx={{ fontSize: '1rem', color: '#f44336' }} /> : (isWord ? <Description sx={{ fontSize: '1rem', color: '#1976d2' }} /> : null)}
              <Tooltip title={`View Document ${urls.length > 1 ? index + 1 : ''}`}>
                <IconButton
                  size="small"
                  color="info"
                  onClick={() => handlePreview(url)}
                  sx={{ bgcolor: '#e3f2fd', '&:hover': { bgcolor: '#bbdefb' }, width: 24, height: 24 }}
                >
                  <Visibility sx={{ fontSize: '0.875rem' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={`Download Document ${urls.length > 1 ? index + 1 : ''}`}>
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => handleDownload(url, title)}
                  sx={{ bgcolor: '#e8f5e9', '&:hover': { bgcolor: '#c8e6c9' }, width: 24, height: 24 }}
                >
                  <Download sx={{ fontSize: '0.875rem' }} />
                </IconButton>
              </Tooltip>
            </MDBox>
          );
        })}
      </MDBox>
    );
  };

  DocumentCell.propTypes = {
    value: PropTypes.any,
    title: PropTypes.string.isRequired,
  };

  // Cell components with PropTypes
  const MonthCell = ({ cell }) => cell.getValue() ? dayjs(cell.getValue(), "YYYY-MM").format("MMM YYYY") : "-";
  MonthCell.propTypes = {
    cell: PropTypes.shape({
      getValue: PropTypes.func.isRequired,
    }).isRequired,
  };

  const DateCell = ({ cell }) => cell.getValue() ? dayjs(cell.getValue()).format("DD-MM-YYYY") : "-";
  DateCell.propTypes = {
    cell: PropTypes.shape({
      getValue: PropTypes.func.isRequired,
    }).isRequired,
  };

  const ActionCell = ({ row }) => (
    <IconButton onClick={() => navigate(`/claims/edit/${row.original.id}/${row.original.user_id}`)}>
      <Edit color="warning" />
    </IconButton>
  );
  ActionCell.propTypes = {
    row: PropTypes.shape({
      original: PropTypes.shape({
        id: PropTypes.number.isRequired,
        user_id: PropTypes.number.isRequired,
      }).isRequired,
    }).isRequired,
  };

  // Document Cell wrappers with PropTypes
  const AttendanceSheetCell = ({ cell }) => <DocumentCell value={cell.getValue()} title="Attendance Sheet" />;
  AttendanceSheetCell.propTypes = {
    cell: PropTypes.shape({
      getValue: PropTypes.func.isRequired,
    }).isRequired,
  };

  const StipendClaimCell = ({ cell }) => <DocumentCell value={cell.getValue()} title="Stipend Claim" />;
  StipendClaimCell.propTypes = {
    cell: PropTypes.shape({
      getValue: PropTypes.func.isRequired,
    }).isRequired,
  };

  const TravelBillsCell = ({ cell }) => <DocumentCell value={cell.getValue()} title="Travel Bills" />;
  TravelBillsCell.propTypes = {
    cell: PropTypes.shape({
      getValue: PropTypes.func.isRequired,
    }).isRequired,
  };

  const ReviewTABillsCell = ({ cell }) => <DocumentCell value={cell.getValue()} title="Review TA Bills" />;
  ReviewTABillsCell.propTypes = {
    cell: PropTypes.shape({
      getValue: PropTypes.func.isRequired,
    }).isRequired,
  };

  const userColumns = useMemo(() => [
    {
      accessorKey: "month",
      header: "Claim Month",
      size: 160,
      Cell: MonthCell,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
    },
    {
      accessorKey: "created_at",
      header: "Submitted On",
      size: 200,
      Cell: DateCell,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
    },
    {
      accessorKey: "attendance_sheet",
      header: "Attendance Sheet",
      size: 180,
      enableSorting: false,
      enableColumnFilter: false,
      Cell: AttendanceSheetCell,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
    },
    {
      accessorKey: "stipend_claim",
      header: "Stipend Claim",
      size: 180,
      enableSorting: false,
      enableColumnFilter: false,
      Cell: StipendClaimCell,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
    },
    {
      accessorKey: "travel_bills",
      header: "Travel Bills",
      size: 180,
      enableSorting: false,
      enableColumnFilter: false,
      Cell: TravelBillsCell,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
    },
    {
      accessorKey: "review_ta_bills",
      header: "Review TA Bills",
      size: 180,
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ReviewTABillsCell,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
    },
    {
      id: "action",
      header: "Action",
      size: 100,
      Cell: ActionCell,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
    }
  ], [navigate])

  return (
    <DashboardLayout>
      <MDBox mt={3} />
      <Card sx={{ mb: 2 }}>
        <MDBox pt={2} px={2}>
          <Grid container spacing={1} display={"flex"} alignItems={"center"}>
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <MDTypography variant="h5" fontWeight="medium">
                Claims List
              </MDTypography>
            </Grid>
            {meData?.role_id !== 1 && (
              <Grid size={{ xs: 12, sm: 6, md: 6 }} display="flex" justifyContent="flex-end">
                <MDButton
                  color={"dark"}
                  onClick={() => navigate("/claims/add")}
                  sx={{
                    color: "white !important",
                    background: "linear-gradient(195deg, #0b6750, #0b6750)",
                  }}
                >
                  Attach Claims
                </MDButton>
              </Grid>
            )}
          </Grid>
        </MDBox>
        <MDBox px={2} pt={0.5}>
          <Grid container spacing={3}>
            {/* Admin Filters - Adjusted Grid sizes */}
            {(meData?.role_id === 1 || meData?.role_id === 2) && (
              <>
                <Grid item size={{ xs: 12, md: 4 }}>
                  <MDAutocomplete
                    label="Select Claims"
                    options={claimOptions}
                    value={filters.claimType}
                    onChange={(val) => setFilters((f) => ({ ...f, claimType: val }))}
                    isFilter
                  />
                </Grid>
                <Grid item size={{ xs: 12, md: 4 }}>
                  <MDAutocomplete
                    label="Select User"
                    options={usersData || []}
                    value={filters.userName}
                    onChange={(val) => setFilters((f) => ({ ...f, userName: val }))}
                    isFilter
                  />
                </Grid>
                <Grid item size={{ xs: 12, md: 4 }}>
                  <MDAutocomplete
                    label="Select District"
                    options={districtData || []}
                    value={filters.district}
                    onChange={(val) => setFilters((f) => ({ ...f, district: val }))}
                    isFilter
                  />
                </Grid>
              </>
            )}

            <Grid item size={{ xs: 12, md: 4 }}>
              <MDTypography style={{ fontSize: "16px" }} color="dark">
                Month Range
              </MDTypography>
              <TextField
                fullWidth
                size="small"
                value={
                  filters.fromMonth && filters.toMonth
                    ? `${filters.fromMonth.format("MMM YYYY")} - ${filters.toMonth.format("MMM YYYY")}`
                    : ""
                }
                placeholder="Select Month Range"
                onClick={(e) => {
                  setAnchorEl(e.currentTarget);
                  setOpenMonthPicker((prev) => !prev);
                }}
                InputProps={{
                  readOnly: true,
                  sx: { cursor: "pointer", backgroundColor: "#fff", height: "45px" },
                }}
              />
              <Popper
                open={openMonthPicker}
                anchorEl={anchorEl}
                placement="bottom-start"
                style={{ zIndex: 1300 }}
              >
                <ClickAwayListener onClickAway={() => setOpenMonthPicker(false)}>
                  <Paper elevation={3} sx={{ p: 2, mt: 1, display: "flex", gap: 2 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="From Month"
                        views={["year", "month"]}
                        value={filters.fromMonth}
                        onChange={(val) => setFilters((f) => ({ ...f, fromMonth: val }))}
                        minDate={minDateLimit}
                        maxDate={dayjs().add(1, 'year').endOf('year')}
                        shouldDisableYear={(year) => year.isAfter(dayjs(), 'year')}
                        shouldDisableMonth={(month) => month.isAfter(dayjs(), 'month')}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                            InputProps: {
                              readOnly: true,
                              sx: { fontSize: "14px", height: "45px" },
                            },
                          },
                        }}
                      />
                      <DatePicker
                        label="To Month"
                        views={["year", "month"]}
                        value={filters.toMonth}
                        onChange={(val) => setFilters((f) => ({ ...f, toMonth: val }))}
                        maxDate={dayjs().add(1, 'year').endOf('year')}
                        shouldDisableYear={(year) => year.isAfter(dayjs(), 'year')}
                        shouldDisableMonth={(month) => month.isAfter(dayjs(), 'month')}
                        minDate={filters.fromMonth || minDateLimit}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                            InputProps: {
                              readOnly: true,
                              sx: { fontSize: "14px", height: "45px" },
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Paper>
                </ClickAwayListener>
              </Popper>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox p={2}>
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item>
              <MDButton color="info" onClick={handleSearch}>
                Search
              </MDButton>
            </Grid>
            <Grid item>
              <MDButton color="error" variant="outlined" onClick={handleReset}>
                Cancel
              </MDButton>
            </Grid>
          </Grid>
        </MDBox>
      </Card>

      <Card>
        <MaterialReactTable
          columns={meData?.role_id === 1 ? columns : userColumns} // Switch columns based on role
          data={data} // Data should be appropriate for role
          getRowId={(row) => row.id || row.user_id}
          enableGlobalFilter={false}
          enableFullScreenToggle={false}
          enableDensityToggle={false}
          initialState={{ showColumnFilters: false }}
          displayColumnDefOptions={{
            "mrt-row-numbers": {
              size: 40,
              maxSize: 40,
              position: "left",
              muiTableHeadCellProps: { align: "center" },
              muiTableBodyCellProps: { align: "center" },
            },
          }}
          layoutMode="grid"
          enableRowNumbers
          enableStickyHeader
          manualFiltering
          manualPagination
          manualSorting
          rowCount={rowCount}
          onPaginationChange={setPagination}
          onSortingChange={setSorting}
          onColumnFiltersChange={setColumnFilters}
          state={{ isLoading: loading, pagination, sorting }}
          muiTableHeadProps={{
            sx: {
              padding: "0px",
              backgroundColor: "#011F3A",
            },
          }}
          enableColumnActions={false}
          muiTableHeadCellProps={{
            sx: {
              backgroundColor: "#004030",
              color: "#fff",
              fontWeight: "bold",
              position: "sticky",
              top: 0,
              zIndex: 1,
              "& .MuiTableSortLabel-root": {
                "& .MuiTableSortLabel-icon": {
                  color: "#fff !important",
                },
              },
              "& .MuiInputBase-input": {
                color: "#fff !important",
                fontSize: "12px",
                "::placeholder": {
                  color: "#e0e0e0",
                  opacity: 1,
                },
              },
              "& .MuiInputBase-root.Mui-focused .MuiInputBase-input::placeholder": {
                color: "#ffffff",
              },
              "& .MuiIconButton-root": {
                color: "#fff",
              },
              "& .MuiInput-underline:before": {
                borderBottomColor: "#e0e0e0",
              },
              "& .MuiInput-underline:after": {
                borderBottomColor: "#fff",
              },
              "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                borderBottomColor: "#fff",
              },
              "& .MuiCheckbox-root": {
                color: "#fff",
              },
            },
          }}
          muiTableBodyCellProps={{
            sx: {
              padding: "4px 8px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          }}
          muiTableBodyRowProps={({ row }) => ({
            sx: {
              backgroundColor: row.index % 2 === 0 ? "#fafafa" : "#f0f0f0",
            },
          })}
          renderTopToolbarCustomActions={({ table }) => (
            <Box
              sx={{
                display: "flex",
                gap: "1rem",
                p: "0.5rem",
                flexWrap: "wrap",
                marginLeft: "auto",
              }}
            >
              {meData?.role_id !== 1 && (
                <Tooltip title={"Export data to excel sheet"} arrow>
                  <Button
                    onClick={handleExportData}
                    sx={{
                      background:
                        "linear-gradient(90deg, rgba(2, 183, 255, 0.65) 30%,rgb(110, 199, 235) 100%)",
                      color: "#f4f4f4",
                      borderRadius: "8px",
                      padding: "10px 20px",
                      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                      transition: "background 0.3s, transform 0.3s",
                      "&:hover": {
                        background:
                          "linear-gradient(90deg, rgba(55, 198, 254, 0.97) 30%,rgb(39, 169, 220) 100%))",
                        transform: "scale(1.05)",
                      },
                      "&:active": {
                        transform: "scale(0.95)",
                      },
                    }}
                    startIcon={<IconTableExport />}
                  >
                    {"Export To Excel"}
                  </Button>
                </Tooltip>
              )}
            </Box>
          )}
          muiTopToolbarProps={{
            sx: {
              backgroundColor: "#fff",
              boxShadow: "none",
            },
          }}
        />
      </Card>

      {/* PDF Viewer Dialog */}
      <Dialog open={Boolean(viewPdfUrl)} onClose={handleClosePdf} maxWidth="lg" fullWidth>
        <DialogTitle display="flex" justifyContent="space-between">
          Document Preview{" "}
          <IconButton onClick={handleClosePdf}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: "60vh" }}>
          {viewPdfUrl && (
            <iframe
              src={viewPdfUrl}
              width="100%"
              height="100%"
              style={{ border: "none" }}
              title="PDF"
            />
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={Boolean(message.status)}
        autoHideDuration={3000}
        onClose={() => setMessage({ status: "", title: "", msg: "" })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setMessage({ status: "", title: "", msg: "" })}
          severity={message.status}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {message.msg}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default ClaimsList;
